import { memo, useState } from "react";
import Demo from "@coworker/apprestructured/src/task-management/demo/pages/ComponentDemoPage/demos/Demo/Demo";
import Name from "@coworker/apprestructured/src/task-management/demo/pages/ComponentDemoPage/demos/Name/Name";
import Highlight from "./Highlight/Highlight";
import RefillQuantity from "@coworker/apprestructured/src/task-management/components/RefillQuantity/RefillQuantity";
import Actions from "./Actions/Actions";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { UnitType } from "@coworker/apprestructured/src/task-management/components/RefillQuantity/constants";

const sanitizeNumber = (value: number) => {
  return /^[0-9]*$/.test(String(value)) ? Math.max(value, 0) : 0;
};

const makeNumericOnChange = (setFn: (value: number) => void) => {
  return (value: number) => {
    setFn(sanitizeNumber(value));
  };
};

function RefillQuantityDemo() {
  const [available, setAvailable] = useState(174);
  const [pieces, setPieces] = useState(57);
  const [multipacks, setMultipacks] = useState(9);
  const [multipackValue, setMultipackValue] = useState(25);
  const [pallets, setPallets] = useState(2);
  const [palletValue, setPalletValue] = useState(100);
  const [maxPieces, setMaxPieces] = useState(217);
  const [activeUnit, setActiveUnit] = useState(UnitType.Pieces);

  const panelConfigs = [
    {
      label: "Pieces",
      panelProps: {
        type: UnitType.Pieces,
        value: pieces,
        onChange: makeNumericOnChange(setPieces),
        suffixLabel: "Pieces",
        max: maxPieces,
        current: pieces,
        isError: pieces > maxPieces,
        isWarning: pieces > available,
        errorMsg: "Exceeding max pieces",
        warningMsg: "Exceeding available stock",
      },
    },
    {
      label: "Multipack",
      panelProps: {
        type: UnitType.Multipack,
        value: multipacks,
        onChange: makeNumericOnChange(setMultipacks),
        infoText: `1 Multipack = ${multipackValue} pieces`,
        suffixLabel: "Multipacks",
        max: maxPieces,
        current: multipacks * multipackValue,
        isError: multipacks * multipackValue > maxPieces,
        isWarning: multipacks * multipackValue > available,
        errorMsg: "Exceeding max pieces",
        warningMsg: "Exceeding available stock",
      },
    },
    {
      label: "Pallet",
      panelProps: {
        type: UnitType.Pallet,
        value: pallets,
        onChange: makeNumericOnChange(setPallets),
        infoText: `1 Pallet = ${palletValue} pieces`,
        suffixLabel: "Pallets",
        max: maxPieces,
        current: pallets * palletValue,
        isError: pallets * palletValue > maxPieces,
        isWarning: pallets * palletValue > available,
        errorMsg: "Exceeding max pieces",
        warningMsg: "Exceeding available stock",
      },
    },
  ];

  return (
    <Demo>
      <Name>RefillQuantity</Name>
      <Highlight>
        <RefillQuantity
          activeUnit={activeUnit}
          onUnitChange={setActiveUnit}
          panelConfigs={panelConfigs}
        />
      </Highlight>
      <Actions>
        <FixaInputField
          label="available"
          type="number"
          id="refillquantitydemo-available"
          value={String(available)}
          onChange={(e) => setAvailable(Number(e.target.value))}
        />
        <FixaInputField
          label="maxPieces"
          type="number"
          id="refillquantitydemo-maxpieces"
          value={String(maxPieces)}
          onChange={(e) => setMaxPieces(Number(e.target.value))}
        />
        <FixaInputField
          label="multipackValue"
          type="number"
          id="refillquantitydemo-multipackvalue"
          value={String(multipackValue)}
          onChange={(e) => setMultipackValue(Number(e.target.value))}
        />
        <FixaInputField
          label="palletValue"
          type="number"
          id="refillquantitydemo-palletvalue"
          value={String(palletValue)}
          onChange={(e) => setPalletValue(Number(e.target.value))}
        />
      </Actions>
    </Demo>
  );
}

export default memo(RefillQuantityDemo);
